import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Save, Loader2, Bot, Upload, Trash2, FileText } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PrototypeConfig {
  id: string;
  system_prompt: string;
  model: string;
  temperature: number;
  max_tokens: number;
}

interface RagDocument {
  id: string;
  name: string;
  file_url: string;
  created_at: string;
}

const AI_MODELS = [
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (recomendado)' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (económico)' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini' },
  { value: 'openai/gpt-5', label: 'GPT-5' },
];

const DEFAULT_PROMPT = `Eres un asistente experto en prototipado de soluciones de IA. Ayudas a los participantes a diseñar, planificar y crear prototipos de sus proyectos.
Tienes en cuenta el contexto del proyecto, el diagnóstico del usuario y los contenidos disponibles en la plataforma.
Responde de forma clara, práctica y con ejemplos concretos. Usa markdown para formatear tus respuestas.`;

export default function PrototypeAssistantConfigTab() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['prototype_assistant_config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prototype_assistant_config' as any)
        .select('*')
        .limit(1)
        .single();
      if (error) return { id: '', system_prompt: DEFAULT_PROMPT, model: 'google/gemini-2.5-flash', temperature: 0.7, max_tokens: 4096 } as PrototypeConfig;
      return data as unknown as PrototypeConfig;
    },
  });

  const { data: documents = [], isLoading: docsLoading } = useQuery({
    queryKey: ['prototype_rag_documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prototype_rag_documents' as any)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) return [];
      return data as unknown as RagDocument[];
    },
  });

  const [form, setForm] = useState<Partial<PrototypeConfig> | null>(null);
  const rawActive = { ...{ system_prompt: DEFAULT_PROMPT, model: 'google/gemini-2.5-flash', temperature: 0.7, max_tokens: 4096 }, ...config, ...form };
  const active = { ...rawActive, system_prompt: rawActive.system_prompt?.trim() ? rawActive.system_prompt : DEFAULT_PROMPT };
  const [uploading, setUploading] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async (updates: Partial<PrototypeConfig>) => {
      // Remove rag_context from updates if present
      const { ...cleanUpdates } = updates;
      if (config?.id) {
        const { error } = await supabase
          .from('prototype_assistant_config' as any)
          .update(cleanUpdates)
          .eq('id', config.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('prototype_assistant_config' as any)
          .insert(cleanUpdates);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prototype_assistant_config'] });
      setForm(null);
      toast.success('Configuración del asistente guardada');
    },
    onError: () => toast.error('Error al guardar'),
  });

  const deleteDocMutation = useMutation({
    mutationFn: async (doc: RagDocument) => {
      const path = doc.file_url.split('/prototype-rag/')[1];
      if (path) {
        await supabase.storage.from('prototype-rag').remove([path]);
      }
      const { error } = await supabase
        .from('prototype_rag_documents' as any)
        .delete()
        .eq('id', doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prototype_rag_documents'] });
      toast.success('Documento eliminado');
    },
    onError: () => toast.error('Error al eliminar documento'),
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExts = ['.pdf', '.txt', '.md'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExts.includes(ext)) {
      toast.error('Solo se permiten archivos PDF, TXT o MD');
      return;
    }

    setUploading(true);
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('prototype-rag')
        .upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('prototype-rag').getPublicUrl(fileName);

      const { data: docRecord, error: insertError } = await supabase
        .from('prototype_rag_documents' as any)
        .insert({ name: file.name, file_url: urlData.publicUrl })
        .select()
        .single();
      if (insertError) throw insertError;

      const isPdf = ext === '.pdf';
      let textContent = '';
      if (!isPdf) {
        textContent = await file.text();
      }

      await supabase.functions.invoke('process-prototype-document', {
        body: {
          document_id: (docRecord as any).id,
          ...(isPdf ? { file_url: urlData.publicUrl } : { text_content: textContent }),
        },
      });

      qc.invalidateQueries({ queryKey: ['prototype_rag_documents'] });
      toast.success('Documento cargado y procesado');
    } catch (err: any) {
      console.error('Upload error:', err);
      toast.error('Error al subir el documento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            Configuración del Asistente de Prototipo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>System Prompt</Label>
            <Textarea
              className="min-h-[200px] font-mono text-xs"
              value={active.system_prompt}
              onChange={(e) => setForm(prev => ({ ...prev, system_prompt: e.target.value }))}
              placeholder="Instrucciones para el asistente de prototipado..."
            />
            <p className="text-xs text-muted-foreground">
              El contexto del proyecto y los documentos RAG se agregan automáticamente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Modelo IA</Label>
              <Select
                value={active.model}
                onValueChange={(v) => setForm(prev => ({ ...prev, model: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Temperatura: {active.temperature}</Label>
              <Slider
                value={[active.temperature]}
                onValueChange={([v]) => setForm(prev => ({ ...prev, temperature: v }))}
                min={0}
                max={1}
                step={0.1}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground">0 = preciso, 1 = creativo</p>
            </div>

            <div className="space-y-2">
              <Label>Max Tokens</Label>
              <Input
                type="number"
                value={active.max_tokens}
                onChange={(e) => setForm(prev => ({ ...prev, max_tokens: parseInt(e.target.value) || 4096 }))}
              />
            </div>
          </div>

          {form && (
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              <Button onClick={() => {
                if (form.system_prompt !== undefined && !form.system_prompt?.trim()) {
                  toast.error('El prompt no puede estar vacío');
                  return;
                }
                saveMutation.mutate(form);
              }} disabled={saveMutation.isPending}>
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Documentos RAG
            </CardTitle>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.md"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Subir Archivo
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Sube documentos (PDF, TXT, MD) que el asistente usará como contexto para guiar en el prototipado.
          </p>
        </CardHeader>
        <CardContent>
          {docsLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : documents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No hay documentos cargados</p>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/30">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(doc.created_at), "d MMM yyyy", { locale: es })}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteDocMutation.mutate(doc)}
                    disabled={deleteDocMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
